import React, { useState } from 'react'
import { CircularProgress, TextField, TextFieldProps } from '@material-ui/core'
import {
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
} from '@material-ui/lab'

interface AutocompletePropsExtension {
  onChange?: (
    event: React.ChangeEvent<{}>,
    value: any,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<any>
  ) => void
  multiple?: boolean
  label?: any
  getOptionLabel?: (option: any) => string
  renderOption?: (
    option: any,
    state: AutocompleteRenderOptionState
  ) => React.ReactNode
  value?: any
  disabled?: boolean
  defaultValue?: any
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode
  TextFieldProps?: TextFieldProps
  customItemColor?: string
  setCustomItemColor?: any
  optionsLoading: any
  fullWidth?: boolean
  key?: any
  required?: boolean
}

interface PaginatedAutocompleteProps extends AutocompletePropsExtension {
  options: any[]
  setOptions: any
  searchVariable: any
  fetchMoreOptions: any
  totalLength: number // Important to act as a stopper
  setValue: any
  name: string
  errorsBool?: boolean
  errorMessage?: string
}

export const PaginatedAutocomplete = ({
  options, // initial array of data from API
  setOptions,
  searchVariable, // searching variable (defined in the API)
  key,
  fetchMoreOptions,
  onChange,
  getOptionLabel,
  renderOption,
  value,
  disabled,
  defaultValue,
  label,
  customItemColor, // For AP and GP (Blue-colored labels)
  optionsLoading,
  fullWidth,
  totalLength,
  setValue,
  name,
  required = false,
  errorsBool,
  errorMessage,
}: PaginatedAutocompleteProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [debounceTimer, setDebounceTimer] = useState(null)

  const loadMoreOptions = async (inputValue = '', resetOffset = false) => {
    console.log(options?.length, totalLength, inputValue)
    if (options?.length <= totalLength) {
      try {
        await fetchMoreOptions({
          variables: {
            skip: resetOffset ? 0 : options?.length,
            take: 30,
            [searchVariable]: inputValue,
          },
        })
      } catch (e) {
        console.error('Error: ', e)
      } finally {
        setIsLoading(false)
      }
    }
  }

  const handleScroll = event => {
    const bottom =
      Math.floor(event.target.scrollHeight - event.target.scrollTop) <=
      event.target.clientHeight
    if (bottom && !isLoading) {
      loadMoreOptions(inputValue)
    }
  }

  // Debouncing manipulation on input change
  const handleInputChange = (_, newInputValue, reason) => {
    setValue(name, null)
    setInputValue(newInputValue)
    if (['', undefined, null]?.includes(newInputValue)) {
      setOptions([])
      fetchMoreOptions()
    } else if (
      reason === 'input' &&
      !['', undefined, null]?.includes(newInputValue)
    ) {
      setOptions([])
      setIsLoading(true)
      if (debounceTimer) {
        // Clear ongoing timeout when input is done within the new Window.setTimeout() period (refer below)
        clearTimeout(debounceTimer) // clearTimeout based on debounceTimer's timeoutID
      }

      const newTimer = setTimeout(() => {
        loadMoreOptions(newInputValue, true)
      }, 1500)

      setDebounceTimer(newTimer) // Assign debounceTimer with new TimeoutID
    }
  }

  return (
    <Autocomplete
      options={options}
      getOptionLabel={getOptionLabel}
      fullWidth={fullWidth || false}
      value={value}
      {...(defaultValue && (defaultValue = { defaultValue }))} // defaultValue is not necessarily called.
      key={key}
      onChange={onChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      ListboxProps={{ onScroll: handleScroll }}
      loading={isLoading}
      noOptionsText={isLoading ? 'Loading...' : 'No options'}
      renderInput={params => (
        <TextField
          {...params}
          fullWidth
          style={{ width: '100%' }}
          required={required}
          label={label}
          margin="normal"
          InputProps={{
            ...params.InputProps,
            style: { color: customItemColor ?? '' },
            endAdornment: (
              <>
                {isLoading || optionsLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          error={errorsBool}
          helperText={errorMessage}
        />
      )}
      renderOption={renderOption}
      disabled={disabled ? disabled : false}
    />
  )
}
